import { Drums } from '@ezchops/drums';
import { SheetEvaluator } from '@ezchops/game/lib/practice';
import range from 'lodash/range';
import sample from 'lodash/sample';
import { useEffect, useMemo } from 'react';
import { useReplayBot } from './replayBot';
export function useDemo(clock, clockState, evaluator, practiceMode, hitScheduler) {
    const enabled = practiceMode == 'demo';
    useEffect(() => {
        if (enabled) {
            generateDemoHits(evaluator);
            return () => {
                evaluator.reset();
            };
        }
    }, [enabled, evaluator, practiceMode]);
    const schedule = useMemo(() => enabled ? hitScheduler : undefined, [enabled, hitScheduler]);
    useReplayBot(evaluator.timing, clock, clockState, evaluator, schedule);
}
export function generateDemoHits(evaluator) {
    if (evaluator instanceof SheetEvaluator) {
        const { timing, ticks } = evaluator;
        const hitGenerator = grooveHitGenerator(evaluator);
        const hits = ticks.flatMap(({ notes }, tick) => {
            const timestamp = timing.tickTimestamp(tick);
            return notes.flatMap(note => hitGenerator(timestamp, note));
        });
        evaluator.push(...hits);
    }
    else {
        const { timing, evaluation } = evaluator;
        const hitGenerator = fillHitGenerator(evaluator);
        const ticks = evaluation.length == 0
            ? timing.ticksPerMeasure * 64
            : evaluation.length;
        const hits = range(0, ticks).flatMap((absTick) => {
            const timestamp = timing.tickTimestamp(absTick);
            const note = {
                dynamics: null, mode: null, note: null,
                sticking: null, tick: absTick, lag: null
            };
            return [note].flatMap(note => hitGenerator(timestamp, note));
        });
        evaluator.push(...hits);
    }
}
function grooveHitGenerator(evaluator) {
    let improIndex = undefined;
    let prevImproNote = undefined;
    return (expectedTimestamp, expectedHit) => {
        const timestamp = expectedTimestamp;
        const rightHand = (improIndex ?? 0) % 2 == 0;
        const note = expectedHit.pitch ?? fillNote(prevImproNote, rightHand, improIndex ?? 0);
        if (expectedHit.pitch == null) {
            improIndex = (improIndex ?? -1) + 1;
            prevImproNote = note;
        }
        const value = evaluator.hitValue(expectedHit.dynamics);
        return note != null ? [{ timestamp, note, value }] : [];
    };
}
function fillHitGenerator(evaluator) {
    const { timing } = evaluator;
    return (expectedTimestamp, expectedHit) => {
        if (expectedHit.note != null) {
            const timestamp = expectedTimestamp;
            const value = evaluator.hitValue(expectedHit.dynamics ?? undefined);
            return [{ timestamp, note: expectedHit.note, value }];
        }
        else {
            const position = timing.tickPosition(expectedHit.tick);
            const timestamp = expectedTimestamp;
            const hits = [];
            const value = evaluator.hitValue(expectedHit.dynamics ?? undefined);
            if (position.absTick == 0) {
                hits.push({ timestamp, note: Drums.CRASH, value });
            }
            else {
                hits.push({ timestamp, note: Drums.CLOSED_HI_HAT, value });
            }
            if (position.tick == 0) {
                hits.push({ timestamp, note: Drums.KICK, value });
            }
            if (position.tick == 0 && position.beat % 2 == 1) {
                hits.push({ timestamp, note: Drums.SNARE, value });
            }
            return hits;
        }
    };
}
function fillNote(prevNote, rightHand, index) {
    switch (prevNote) {
        case undefined:
            return index == 0
                ? sample([Drums.HIGH_TOM, Drums.MID_TOM, Drums.LOW_TOM, Drums.SNARE, Drums.CLOSED_HI_HAT])
                : sample([Drums.HIGH_TOM, Drums.MID_TOM, Drums.LOW_TOM, Drums.SNARE]);
        case Drums.CLOSED_HI_HAT:
            if (rightHand) {
                return sample([Drums.CLOSED_HI_HAT, undefined]);
            }
            else {
                return sample([Drums.SNARE, Drums.CLOSED_HI_HAT]);
            }
        case Drums.HIGH_TOM:
            if (rightHand) {
                return sample([Drums.HIGH_TOM, Drums.MID_TOM, Drums.LOW_TOM, Drums.SNARE, undefined]);
            }
            else {
                return sample([Drums.HIGH_TOM, Drums.SNARE, undefined]);
            }
        case Drums.MID_TOM:
            if (rightHand) {
                return sample([Drums.MID_TOM, Drums.LOW_TOM, undefined]);
            }
            else {
                return sample([Drums.MID_TOM, Drums.HIGH_TOM, Drums.SNARE, undefined]);
            }
        case Drums.LOW_TOM:
            if (rightHand) {
                return sample([Drums.LOW_TOM, undefined]);
            }
            else {
                return sample([Drums.LOW_TOM, Drums.MID_TOM, Drums.HIGH_TOM, Drums.SNARE, undefined]);
            }
        case Drums.SNARE:
            if (rightHand) {
                return sample([Drums.SNARE, Drums.HIGH_TOM, Drums.MID_TOM, Drums.LOW_TOM, undefined]);
            }
            else {
                return sample([Drums.SNARE, Drums.HIGH_TOM, undefined]);
            }
    }
}
//# sourceMappingURL=demo.js.map